import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '@/contexts/AuthContext';
import { useAuth } from '@/contexts/AuthContext';
import { useLang } from '@/contexts/LanguageContext';
import { toast } from 'sonner';
import { Plus, Trash2, CheckCircle, Clock, CalendarDays, MapPin, Clock3 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';

const STATUS_CONFIG = {
    upcoming: { label: 'Mendatang', cls: 'badge-upcoming', icon: Clock },
    completed: { label: 'Selesai', cls: 'badge-completed', icon: CheckCircle },
    cancelled: { label: 'Dibatalkan', cls: 'badge-inactive', icon: null }
};

const emptyForm = { title: '', description: '', date: '', time: '', location: '', agenda: '' };

export default function MeetingsPage() {
    const { hasRole } = useAuth();
    const { t } = useLang();
    const [meetings, setMeetings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);
    const canCreate = hasRole('sekretaris', 'ketua_rt', 'wakil');

    const load = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/meetings`, { withCredentials: true });
            setMeetings(res.data);
        } catch { toast.error('Gagal memuat data pertemuan'); }
        setLoading(false);
    };

    useEffect(() => { load(); }, []);

    const handleSave = async () => {
        if (!form.title || !form.date || !form.time || !form.location) {
            toast.error('Isi judul, tanggal, waktu, dan tempat'); return;
        }
        setSaving(true);
        try {
            await axios.post(`${API}/meetings`, form, { withCredentials: true });
            toast.success('Undangan rapat dibuat dan disiarkan ke semua warga');
            setShowForm(false); setForm(emptyForm); load();
        } catch (e) { toast.error(e.response?.data?.detail || 'Gagal membuat undangan'); }
        setSaving(false);
    };

    const markComplete = async (id) => {
        try {
            await axios.put(`${API}/meetings/${id}`, { status: 'completed' }, { withCredentials: true });
            toast.success('Pertemuan ditandai selesai'); load();
        } catch (e) { toast.error(e.response?.data?.detail || 'Gagal'); }
    };

    const deleteMeeting = async (id) => {
        if (!window.confirm('Hapus undangan ini?')) return;
        try { await axios.delete(`${API}/meetings/${id}`, { withCredentials: true }); toast.success('Undangan dihapus'); load(); }
        catch (e) { toast.error(e.response?.data?.detail || 'Gagal'); }
    };

    const upcomingMeetings = meetings.filter(m => m.status === 'upcoming');
    const pastMeetings = meetings.filter(m => m.status !== 'upcoming');

    return (
        <div className="space-y-5" data-testid="meetings-page">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                    <h1 className="page-header text-2xl">{t.meetingsTitle}</h1>
                    <p className="text-sm text-slate-500 mt-1">{upcomingMeetings.length} pertemuan mendatang</p>
                </div>
                {canCreate && (
                    <Button onClick={() => setShowForm(true)} className="bg-primary hover:bg-primary/90 gap-2" data-testid="create-meeting-btn">
                        <Plus className="w-4 h-4" /> {t.createInvitation}
                    </Button>
                )}
            </div>

            {/* Upcoming */}
            {upcomingMeetings.length > 0 && (
                <div className="space-y-3">
                    <p className="label-overline">Mendatang ({upcomingMeetings.length})</p>
                    {upcomingMeetings.map(m => (
                        <div key={m.id} className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md hover:-translate-y-0.5 transition-all">
                            <div className="flex flex-col md:flex-row md:items-start justify-between gap-3">
                                <div className="flex items-start gap-4 flex-1">
                                    <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center flex-shrink-0">
                                        <CalendarDays className="w-6 h-6 text-purple-600" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap mb-1">
                                            <h3 className="font-bold text-slate-800" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{m.title}</h3>
                                            <span className="text-xs px-2 py-0.5 rounded-full badge-upcoming font-medium">Mendatang</span>
                                        </div>
                                        {m.description && <p className="text-sm text-slate-600 mb-2">{m.description}</p>}
                                        <div className="flex flex-wrap gap-4 text-sm text-slate-500">
                                            <span className="flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5" />{m.date}</span>
                                            <span className="flex items-center gap-1.5"><Clock3 className="w-3.5 h-3.5" />{m.time}</span>
                                            <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" />{m.location}</span>
                                        </div>
                                        {m.agenda && (
                                            <div className="mt-2 p-2.5 bg-slate-50 rounded-lg">
                                                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Agenda</p>
                                                <p className="text-sm text-slate-700 whitespace-pre-line">{m.agenda}</p>
                                            </div>
                                        )}
                                        <p className="text-xs text-slate-400 mt-2">Dibuat oleh: {m.creator_name}</p>
                                    </div>
                                </div>
                                {canCreate && (
                                    <div className="flex gap-2 flex-shrink-0">
                                        <Button variant="outline" size="sm" onClick={() => markComplete(m.id)} className="gap-1.5 h-8 text-xs"
                                            data-testid={`complete-meeting-${m.id}`}>
                                            <CheckCircle className="w-3.5 h-3.5 text-green-600" /> Selesai
                                        </Button>
                                        <button onClick={() => deleteMeeting(m.id)} className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                            data-testid={`delete-meeting-${m.id}`}>
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Past meetings */}
            {pastMeetings.length > 0 && (
                <div className="space-y-3">
                    <p className="label-overline">Riwayat ({pastMeetings.length})</p>
                    <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
                        {pastMeetings.map(m => {
                            const cfg = STATUS_CONFIG[m.status] || STATUS_CONFIG.completed;
                            return (
                                <div key={m.id} className="p-4 flex items-center gap-4 hover:bg-slate-50 transition-colors">
                                    <CalendarDays className="w-5 h-5 text-slate-300 flex-shrink-0" />
                                    <div className="flex-1 min-w-0">
                                        <p className="font-semibold text-slate-700 text-sm">{m.title}</p>
                                        <p className="text-xs text-slate-400 mt-0.5">{m.date} • {m.time} • {m.location}</p>
                                    </div>
                                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${cfg.cls}`}>{cfg.label}</span>
                                    {canCreate && (
                                        <button onClick={() => deleteMeeting(m.id)} className="p-1.5 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded transition-colors">
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {loading && (
                <div className="space-y-3">
                    {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-32 w-full rounded-xl" />)}
                </div>
            )}

            {!loading && meetings.length === 0 && (
                <div className="bg-white rounded-xl border border-slate-200 py-16 text-center">
                    <CalendarDays className="w-12 h-12 text-slate-200 mx-auto mb-3" />
                    <p className="text-slate-400 font-medium">Belum ada undangan rapat</p>
                    {canCreate && <p className="text-slate-400 text-sm mt-1">Klik "Buat Undangan Baru" untuk memulai</p>}
                </div>
            )}

            <Dialog open={showForm} onOpenChange={setShowForm}>
                <DialogContent className="max-w-md">
                    <DialogHeader><DialogTitle>Buat Undangan Rapat Baru</DialogTitle></DialogHeader>
                    <div className="space-y-4 py-2">
                        <div className="space-y-1.5">
                            <Label>{t.meetingTitle2} *</Label>
                            <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                                placeholder="Rapat Warga RT..." data-testid="meeting-title-input" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                                <Label>{t.meetingDate} *</Label>
                                <Input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                                    data-testid="meeting-date-input" />
                            </div>
                            <div className="space-y-1.5">
                                <Label>{t.meetingTime} *</Label>
                                <Input type="time" value={form.time} onChange={e => setForm(f => ({ ...f, time: e.target.value }))}
                                    data-testid="meeting-time-input" />
                            </div>
                        </div>
                        <div className="space-y-1.5">
                            <Label>{t.meetingLoc} *</Label>
                            <Input value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))}
                                placeholder="Balai RT / Posyandu..." data-testid="meeting-loc-input" />
                        </div>
                        <div className="space-y-1.5">
                            <Label>{t.meetingDesc}</Label>
                            <Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                                placeholder="Keterangan pertemuan..." rows={2} data-testid="meeting-desc-input" />
                        </div>
                        <div className="space-y-1.5">
                            <Label>{t.agenda}</Label>
                            <Textarea value={form.agenda} onChange={e => setForm(f => ({ ...f, agenda: e.target.value }))}
                                placeholder="1. Pembukaan&#10;2. Laporan Keuangan&#10;3. ..." rows={3} data-testid="meeting-agenda-input" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowForm(false)}>{t.cancel}</Button>
                        <Button onClick={handleSave} disabled={saving} className="bg-primary hover:bg-primary/90" data-testid="save-meeting-btn">
                            {saving ? 'Menyimpan...' : 'Buat & Siarkan'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
